/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import {useItemVariant} from '@salesforce/retail-react-app/app/components/item-variant'
import PropTypes from 'prop-types'
import {Heading, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '../link'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {getProductLineItemUrl} from '../../utils/product-utils'
import useCartItemNavigation from '../../hooks/use-cart-item-navigation'

/**
 * In the context of a cart product item variant, this components simply renders
 * the item's name using the cart item field `productName`. We use this field
 * rather than variant `name` field as the variant detail may not yet be
 * available upon rendering.
 */
const ItemName = (props) => {
    const cartStyles = useMultiStyleConfig('CartStyles')
    const {product} = props || {}
    const {data: basket} = useCurrentBasket()
    const variant = useItemVariant()
    const productUrlFormatted = getProductLineItemUrl(basket, variant)
    const breadcrumb =
        variant?.c_breadcrumb
            ?.map((item) => item?.htmlValue)
            ?.reverse()
            ?.join('/') || ''
    const getLinkProps = (enabled, isExternal) =>
        enabled
            ? {
                  to: productUrlFormatted?.to || productUrlFormatted?.href,
                  isExternal
              }
            : {}
    const hasEngraving = variant?.c_driveRecipeID
    const isColorBarProduct =
        variant?.c_fluidConfigureImageURL &&
        variant?.c_fluidConfigureRecipeID &&
        variant?.c_fluidConfigureProductID

    const colorBarProps = getLinkProps(isColorBarProduct, true)
    const engravingProps = getLinkProps(hasEngraving, false)
    const disabledProps = variant?.c_charitySelection
        ? {
              tabIndex: -1,
              'aria-disabled': true,
              role: 'text'
          }
        : {}
    const handleClick = useCartItemNavigation({
        product,
        productUrl: productUrlFormatted?.to || productUrlFormatted?.href,
        breadcrumb,
        isExternal: isColorBarProduct
    })

    return (
        <Heading as="h2" fontSize="md" className="product-name-heading">
            <Link
                {...props}
                {...productUrlFormatted}
                {...cartStyles.lineItems.item.name}
                onClick={handleClick}
                {...colorBarProps}
                {...engravingProps}
                {...disabledProps}
                pointerEvents={variant?.c_charitySelection ? 'none' : 'unset'}
                className="product-name-link"
            >
                {variant.productName || variant.name}
            </Link>
        </Heading>
    )
}

export default ItemName

ItemName.propTypes = {
    product: PropTypes.object
}
